import { FC, useMemo } from 'react';
import { Box } from '@material-ui/core';
import { WbTooltip } from '@agilelab/plugin-wb-platform';
import ReplayIcon from '@material-ui/icons/Replay';
import DeleteIcon from '@material-ui/icons/Delete';
import { AccessToken, SafeAccessToken } from '@agilelab/plugin-wb-auth-backend';
import {
  isoDateToDisplayString,
  WbBadge,
  WbTable,
  TableCellProps,
  WbButton,
  createActionsColumn,
} from '@agilelab/plugin-wb-platform';

interface AccessTokenTableProps {
  accessTokens: SafeAccessToken[];
  onDeleteToken: (token: SafeAccessToken) => void;
  onRotateToken: (token: SafeAccessToken) => void;
}

const isExpired = (token: SafeAccessToken) => {
  if (!token.expires_at) {
    return false;
  }

  return new Date(token.expires_at) < new Date();
};

export const AccessTokenTable: FC<AccessTokenTableProps> = ({
  accessTokens,
  onDeleteToken,
  onRotateToken,
}) => {
  const getExpirationDisplay = (token: SafeAccessToken) => {
    if (!token.expires_at) {
      return (
        <WbBadge label="No expiration" variant="stroke">
          NO EXPIRATION
        </WbBadge>
      );
    }

    if (isExpired(token)) {
      return (
        <WbBadge label="EXPIRED" variant="stroke" color="error">
          EXPIRED
        </WbBadge>
      );
    }

    return isoDateToDisplayString(token.expires_at, {
      showHour: false,
      showMinute: false,
      showSecond: false,
    });
  };

  const columns = useMemo(
    (): Array<TableCellProps<AccessToken | SafeAccessToken>> => [
      {
        field: 'expires_at',
        headerName: 'EXPIRES ON (EXCLUSIVE)',
        fieldRender: token => getExpirationDisplay(token),
        sortable: false,
        width: '25%',
      },
      {
        field: 'created_at',
        headerName: 'CREATED',
        fieldRender: token => isoDateToDisplayString(token.created_at),
        sortable: false,
        width: '25%',
      },
      {
        field: 'updated_at',
        headerName: 'LAST UPDATE',
        fieldRender: token => isoDateToDisplayString(token.updated_at),
        sortable: false,
        width: '25%',
      },
      createActionsColumn({
        renderActions: token => (
          <Box
            style={{
              display: 'flex',
              gap: 4,
              justifyContent: 'flex-end',
            }}
          >
            <WbTooltip title="Regenerate token">
              <WbButton
                aria-label="Regenerate token"
                onClick={() => onRotateToken(token)}
                size="small"
                disabled={isExpired(token)}
                variant="text"
                color="default"
                icon={<ReplayIcon fontSize="small" />}
              />
            </WbTooltip>
            <WbTooltip title="Delete token">
              <WbButton
                aria-label="delete token"
                onClick={() => onDeleteToken(token)}
                size="small"
                variant="text"
                color="default"
                icon={<DeleteIcon fontSize="small" />}
              />
            </WbTooltip>
          </Box>
        ),
      }),
    ],
    [onDeleteToken, onRotateToken],
  );

  return (
    <WbTable<SafeAccessToken>
      components={{
        tableContent: {
          columns,
          rows: accessTokens,
        },
      }}
      noDataLabel="No access tokens found"
      stickyHeader={false}
    />
  );
};

AccessTokenTable.displayName = 'AccessTokenTable';
